import React, { ReactElement } from "react";
import "./Timeline.css";

interface EventDetail {
  label?: string;
  value: string;
}

interface TimelineEvent {
  id: string;
  year: number;
  month: string;
  title: string;
  icon: string;
  details: EventDetail[];
  side: "left" | "right";
}

interface TimelineProps {
  events: TimelineEvent[];
  years?: number[];
}

export const Timeline: React.FC<TimelineProps> = ({ events, years }) => {
  const timelineYears = years || [];

  // Helper function to render text with line breaks
  const renderWithLineBreaks = (text: string) : ReactElement[] => {
    const lines = text.split(/\r\n|\r|\n/);
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Group events by year
  const eventsByYear = new Map<number, typeof events>();
  events.forEach((event) => {
    if (!eventsByYear.has(event.year)) {
      eventsByYear.set(event.year, []);
    }
    eventsByYear.get(event.year)!.push(event);
  });

  // Get sorted years from events if not provided
  const yearsToDisplay = timelineYears.length > 0 
    ? timelineYears 
    : Array.from(eventsByYear.keys()).sort((a, b) => a - b);

  return (
    <div className="timeline">
      {yearsToDisplay.map((year, yearIndex) => {
        const yearEvents = eventsByYear.get(year) || [];
        const showYearLabel = yearIndex === 0 || year !== yearsToDisplay[yearIndex - 1];

        return (
          <div key={year} className="timeline-year-block">
            {/* Year Label */}
            {showYearLabel && (
              <div className="year-label-block">
                <div className="year-label">{year}</div>
              </div>
            )}

            {/* Events for this year */}
            {yearEvents.map((event) => (
              <div key={event.id} className="event-block">
                <div className={`event event-${event.side}`}>
                  <div className="event-dot"></div>
                  <div className="event-card">
                    {event.side === "left" ? (
                      <>
                        <div className="event-content">
                          <div className="event-title">{renderWithLineBreaks(event.title)}</div>
                          <div className="event-details">
                            {event.details.map((detail, idx) => (
                              <div key={idx} className="event-detail">
                                {detail.label ? `${detail.label}: ` : ''}{detail.value}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="event-divider"></div>
                        <div className="event-month">
                          <div className="month-label">{event.month}</div>
                          <div className="event-icon">
                            {event.icon.startsWith('/') || event.icon.startsWith('http') ? (
                              <img src={event.icon} alt={event.title} />
                            ) : (
                              event.icon
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="event-month">
                          <div className="month-label">{event.month}</div>
                          <div className="event-icon">
                            {event.icon.startsWith('/') || event.icon.startsWith('http') ? (
                              <img src={event.icon} alt={event.title} />
                            ) : (
                              event.icon
                            )}
                          </div>
                        </div>  
                        <div className="event-divider"></div>
                        <div className="event-content">
                          <div className="event-title">{renderWithLineBreaks(event.title)}</div>
                          <div className="event-details">
                            {event.details.map((detail, idx) => (
                              <div key={idx} className="event-detail">
                                {detail.label ? `${detail.label}: ` : ''}{detail.value}
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};